import {
  ColDef,
  ProcessCellForExportParams,
  GetQuickFilterTextParams,
  ValueFormatterFunc,
  ValueFormatterParams,
} from '@ag-grid-community/core';
import { CellClassParams, CellStyle } from '@ag-grid-community/core/dist/cjs/es5/entities/colDef';
import { agTableValueComparator } from '@creditsights/styleguide-web';
import hexToRgba from 'hex-to-rgba';
import { get } from 'lodash';
import round from 'lodash/round';
import React from 'react';
import { NavLink } from 'react-router-dom';
import { AG_TABLE_RIGHT_ALIGNED_COLUMN_DEF, EM_DASH } from '../../../../Base.constants';
import { RpColumn, RpColumnRenderType } from '../../../../models/RpColumn';
import { getCompanyPageLink } from '../../../../pages/Companies/Companies.logic';

import { MonitorDto } from '../../../../services/dtos/riskProducts/monitor.dto';
import { EsField } from '../../../../services/dtos/riskProducts/rpColumn.dto';
import { getColor } from '../../../../utils/chartUtils';
import { DATE_FORMATS, formatDate } from '../../../../utils/dateUtils';
import { getCompanyTag } from '../../../../utils/tagUtils';
import MetricChangeIcon from '../../common/MetricChangeIcon/MetricChangeIcon';
import { formatAsPctString } from '../../common/MetricChangeIcon/MetricChangeIcon.logic';
import { EXCLUDED_MONITOR_ES_FIELDS } from './Monitor.constants';

const dateFormatter = (params: ValueFormatterParams): string =>
  formatDate(params.value, DATE_FORMATS.MONTH_ABBR_DATE, EM_DASH);

export const getDefaultRpColumnDefByRenderType = (column: RpColumn): Partial<ColDef> | null => {
  switch (column.renderType) {
    case RpColumnRenderType.DATE:
      return {
        valueFormatter: dateFormatter,
        getQuickFilterText: (params) => getCellValue(params),
      };
    case RpColumnRenderType.COLORED:
      return {
        cellStyle: (param: CellClassParams): CellStyle | undefined => {
          const color = getColor(param.value);
          return color ? { backgroundColor: hexToRgba(color, 0.16) } : undefined;
        },
      };
    case RpColumnRenderType.METRIC_CHANGE_INDICATOR:
      return {
        ...AG_TABLE_RIGHT_ALIGNED_COLUMN_DEF,
        cellRenderer: ({ data, valueFormatted }) => {
          return (
            <MetricChangeIcon
              displayValue={valueFormatted}
              value={get(data, column.es_field)}
              previousValue={get(data, column.meta?.previousField)}
            />
          );
        },
        valueFormatter: (params) => {
          if (params.value == null) {
            return EM_DASH;
          }
          return column.meta?.showPercents ? formatAsPctString(params.value, 2) : round(params.value, 0).toString();
        },
        getQuickFilterText: (params) => getCellValue(params),
      };
    case RpColumnRenderType.ROUNDED:
      return {
        ...AG_TABLE_RIGHT_ALIGNED_COLUMN_DEF,
        valueFormatter: (params) => {
          return params.value || params.value === 0
            ? `${round(params.value, column.meta?.precision)}${column.meta?.suffix ?? ''}`
            : EM_DASH;
        },
        getQuickFilterText: (params) => getCellValue(params),
      };
    case RpColumnRenderType.PERCENTAGE:
      return {
        ...AG_TABLE_RIGHT_ALIGNED_COLUMN_DEF,
        valueFormatter: (params) => {
          return params.value || params.value === 0 ? formatAsPctString(params.value, column.meta?.precision) : EM_DASH;
        },
        getQuickFilterText: (params) => getCellValue(params),
      };
    case RpColumnRenderType.DEFAULT:
    default:
      return null;
  }
};

const getColumnDefByRenderType = (column: RpColumn): Partial<ColDef<MonitorDto>> => {
  const colDef = getDefaultRpColumnDefByRenderType(column);
  if (colDef) {
    return colDef;
  }

  switch (column.renderType) {
    case RpColumnRenderType.COMPANY_LINK:
      return {
        pinned: 'left',
        cellRenderer: ({ data }: { data: MonitorDto }) => (
          <NavLink to={getCompanyPageLink(getCompanyTag(data.cs_companyid))}>{data.display_name}</NavLink>
        ),
        comparator: agTableValueComparator,
      };
    case RpColumnRenderType.DEFAULT:
    default:
      return {};
  }
};

export const mapRpColumnToDefinition = (column: RpColumn): ColDef<MonitorDto> => ({
  colId: column.es_field,
  field: column.es_field,
  headerName: column.short_name ?? column.data_field,
  headerTooltip: column.description,
  suppressMenu: true,
  hide: !column.canAccess,
  ...getColumnDefByRenderType(column),
});

export const getColDefForColumns = (esFields: EsField[], columns: RpColumn[]): ColDef<MonitorDto>[] => {
  return columns.filter((column) => esFields.includes(column.es_field)).map(mapRpColumnToDefinition);
};

export const getMonitorManageColumns = (rpColumns: RpColumn[]): RpColumn[] => {
  return rpColumns.filter((item) => !EXCLUDED_MONITOR_ES_FIELDS.includes(item.es_field));
};

export const getCellValue = (params: ProcessCellForExportParams | GetQuickFilterTextParams): string => {
  const colDef = params.column.getColDef();

  const nextParams: ValueFormatterParams = {
    ...params,
    value: params.value,
    data: params.node?.data,
    node: params.node!,
    colDef,
  };

  if (colDef.valueFormatter) {
    return (colDef.valueFormatter as ValueFormatterFunc<MonitorDto>)(nextParams);
  }

  return nextParams.value;
};

export const createExportFileName = (): string => `Monitor_${formatDate(new Date(), DATE_FORMATS.YEAR_MONTH_DAY)}`;
